/**
 * Настройки сообщества: вкладки, общий черновик, сохранение.
 *
 * Вкладка — это только отбор того, что показать. Черновик один на всю
 * страницу и уходит одним запросом: правки на одной вкладке не пропадают
 * при переходе на другую, и сохранять их по частям не нужно.
 */

import { useEffect, useState, type ReactNode } from 'react';
import type { CommunitySettings, WiringSettings } from '@ak/shared';

import { apiPatch } from '../../lib/api.ts';
import { navigate, useLinkHandler } from '../../lib/router.ts';
import { SETTINGS_TABS, settingsTabPath, type SettingsTab } from '../../lib/settings-tabs.ts';
import { useResource } from '../../lib/useResource.ts';
import { useToast } from '../../components/toast.tsx';
import { Button, ErrorState, Panel, PageHeader, Skeleton } from '../../components/ui.tsx';
import { ApplicationsTab } from './applications.tsx';
import { DmTab } from './dm.tsx';
import { EventsTab } from './events.tsx';
import { GeneralTab } from './general.tsx';
import { MessagesTab } from './messages.tsx';
import { RollbacksTab } from './rollbacks.tsx';
import { WiringTab } from './wiring.tsx';
import type { GuildStructure, SettingsResponse, UpdateSetting } from './shared.tsx';

function TabsNav({ current }: { current: SettingsTab }): ReactNode {
  const onLinkClick = useLinkHandler();

  return (
    <nav className="settings-tabs" aria-label="Разделы настроек">
      {SETTINGS_TABS.map((tab) => (
        <a
          key={tab.slug}
          href={settingsTabPath(tab.slug)}
          onClick={onLinkClick}
          className="settings-tab"
          aria-current={tab.slug === current ? 'page' : undefined}
        >
          {tab.label}
        </a>
      ))}
    </nav>
  );
}

export function SettingsPage({ tab }: { tab: SettingsTab }): ReactNode {
  const toast = useToast();
  const { data, loading, error, reload } = useResource<SettingsResponse>(
    '/api/settings',
    (event) => event.type === 'settings.changed',
  );
  /*
   * Список каналов и ролей сервер берёт из Discord, поэтому запрашивается он
   * только на той вкладке, где виден. Раньше настройки были одной страницей
   * и выбора не было; теперь заход за часовым поясом не должен стоить
   * обращения к Discord.
   */
  const structure = useResource<GuildStructure>(tab === 'wiring' ? '/api/guild/structure' : null, () => false);

  const [draft, setDraft] = useState<CommunitySettings | null>(null);
  const [wiringDraft, setWiringDraft] = useState<WiringSettings | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (data !== null) {
      setDraft(data.settings);
      setWiringDraft(data.wiring.overrides);
    }
  }, [data]);

  const dirty = data !== null && draft !== null && JSON.stringify(draft) !== JSON.stringify(data.settings);

  /*
   * Несохранённый черновик переживает переключение вкладок, но не закрытие
   * страницы. Предупреждение — единственное, что здесь можно сделать: сама
   * форма о перезагрузке узнать не успевает.
   */
  useEffect(() => {
    if (!dirty) {
      return;
    }

    const warn = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
    };

    window.addEventListener('beforeunload', warn);
    return () => {
      window.removeEventListener('beforeunload', warn);
    };
  }, [dirty]);

  if (error !== null) {
    return <ErrorState message={error} onRetry={reload} />;
  }

  if (loading || data === null || draft === null || wiringDraft === null) {
    return (
      <Panel>
        <Skeleton rows={10} />
      </Panel>
    );
  }

  const saveSettings = (): void => {
    setBusy(true);
    apiPatch('/api/settings', draft)
      .then(() => {
        toast.success('Настройки сохранены');
        reload();
      })
      .catch((cause: unknown) => {
        toast.error(cause instanceof Error ? cause.message : 'Не удалось сохранить');
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const saveWiring = (): void => {
    setBusy(true);
    apiPatch('/api/settings/wiring', wiringDraft)
      .then(() => {
        toast.success('Привязка обновлена');
        reload();
      })
      .catch((cause: unknown) => {
        toast.error(cause instanceof Error ? cause.message : 'Не удалось сохранить');
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const update: UpdateSetting = (key, value) => {
    setDraft((current) => (current === null ? current : { ...current, [key]: value }));
  };

  const body = ((): ReactNode => {
    switch (tab) {
      case 'general':
        return <GeneralTab draft={draft} update={update} />;
      case 'events':
        return <EventsTab draft={draft} update={update} />;
      case 'applications':
        return <ApplicationsTab draft={draft} update={update} />;
      case 'rollbacks':
        return <RollbacksTab draft={draft} update={update} />;
      case 'messages':
        return <MessagesTab draft={draft} update={update} />;
      case 'dm':
        return <DmTab draft={draft} update={update} />;
      case 'wiring':
        return (
          <WiringTab
            data={data}
            wiringDraft={wiringDraft}
            setWiringDraft={setWiringDraft}
            structure={structure.data}
            onSave={saveWiring}
            busy={busy}
          />
        );
    }
  })();

  return (
    <>
      <PageHeader
        title="Настройки"
        description="Меняются на лету, перезапуск бота не нужен"
        actions={
          <>
            {/* Отметка нужна потому, что вкладок много: изменение может
                остаться на той, которая сейчас не видна. */}
            {dirty && (
              <span className="text-sm" style={{ color: 'var(--warning)' }}>
                Есть несохранённые изменения
              </span>
            )}
            <Button
              onClick={() => {
                navigate('/setup');
              }}
            >
              Разметка сервера
            </Button>
            <Button variant="primary" onClick={saveSettings} busy={busy}>
              Сохранить настройки
            </Button>
          </>
        }
      />

      <TabsNav current={tab} />

      {body}
    </>
  );
}
